import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Trash2, Loader2, Eye } from 'lucide-react';
import { Question } from '../../../api/questions';
import { Subject } from '../../../api/subjects';

interface QuestionsTableProps {
  isDark: boolean;
  questions: Question[];
  subjects: Subject[];
  isLoading: boolean;
  onView: (q: Question) => void;
  onEdit: (q: Question) => void;
  onDelete: (id: string) => void;
}

export function QuestionsTable({
  isDark,
  questions,
  subjects,
  isLoading,
  onView,
  onEdit,
  onDelete,
}: QuestionsTableProps) {
  const subjectMap = React.useMemo(() => {
    const m: Record<string, string> = {};
    subjects.forEach((s) => (m[s.id] = s.name));
    return m;
  }, [subjects]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  const headCls = cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500');
  const rowCls = cn('transition-colors', isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50');

  return (
    <Table>
      <TableHeader>
        <TableRow className={cn(isDark ? 'border-slate-700' : 'border-slate-100')}>
          <TableHead className={headCls}>Question</TableHead>
          <TableHead className={headCls}>Subject</TableHead>
          <TableHead className={headCls}>Type</TableHead>
          <TableHead className={headCls}>Options</TableHead>
          <TableHead className={headCls}>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {questions.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-12 text-slate-500">
              No questions found
            </TableCell>
          </TableRow>
        ) : (
          questions.map((q) => (
            <TableRow key={q.id} className={rowCls}>
              <TableCell className={cn('max-w-md', isDark ? 'text-white' : 'text-[#0F172A]')}>
                <div className="line-clamp-2 text-sm">{q.text}</div>
              </TableCell>
              <TableCell className={cn('text-sm', isDark ? 'text-slate-300' : 'text-slate-700')}>
                {subjectMap[q.subject_id] || '—'}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs">
                  {q.type}
                </Badge>
              </TableCell>
              <TableCell className={cn('text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                {q.options?.length || 0} opts
                {q.sub_questions?.length ? ` • ${q.sub_questions.length} sub` : ''}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onView(q)}
                    className="h-8 px-2"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(q)}
                    className="h-8 px-2"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(q.id)}
                    className="h-8 px-2 text-slate-500 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
